<template>
    <div class="room" :class="{ condensed }">
        <div class="room-name-container">
            <span class="room-name">
                {{ room.name }}
            </span>
        </div>
        <div class="content">
            <div class="labels">
                <span class="label">
                    <fa-icon icon="user" />
                    {{ room.players.length }}/{{ room.numOfPlayers }}
                </span>
                <span class="label" v-if="room.timeLimit" :class="{[`time-limit-${room.timeLimit}`]: true}">
                    <fa-icon icon="clock" class="time-limit-icon" />
                    {{ room.timeLimit }} min
                </span>
                <span class="label" v-if="blitz" :class="{[`blitz-${blitz}`]: true}">
                    <fa-icon icon="bolt-lightning" class="blitz-icon" />{{ blitz }} min
                </span>
                <span class="label">
                    <fa-icon icon="trophy" class="ranked-icon" v-if="room.ranked" />
                    <fa-icon icon="smile" class="ranked-icon" v-if="!room.ranked" />
                </span>
                <fa-icon icon="lock" class="pwd-required-icon" v-if="room.passwordRequired" />
            </div>
            <div class="room-players">
                <div class="room-player"
                    v-for="player in room.players"
                    :key="`room-${room.id}-${player.id}`"
                >
                    <router-link :to="`/matches/${encodeLabel(player.user.username)}`" class="link" target="_blank">
                        {{ player.user.deleted ? '[deleted]' : player.user.username }} <rank :skillRating="player.user.skillRating" />
                    </router-link>
                    <disqualified-alert type="icon" v-if="player.user.disqualified" />
                </div>
            </div>
        </div>
        <div class="game-state">
            <div class="label game-state-label">
                {{ gameState[room.state] }}
            </div>
        </div>
        <div class="setting-labels game-modes">
            <span class="label mode" v-if="nobilityExpansion">
				<span class="icon-wrapper tooltip-trigger" v-if="expansions.length >= 4"
					>
					<fa-icon
						icon="crown"
						class="icon expansion-icon nobility"
					/>
					<tooltip value="Nobility Expansion" />
				</span>
				<span v-else>
					Nobility
				</span>
            </span>
            <span class="label mode" v-if="siegeExpansion">
				<span class="icon-wrapper tooltip-trigger" v-if="expansions.length >= 4">
					<icon
						icon="fort"
						class="icon expansion-icon siege"
					/>
					<tooltip value="Siege Expansion" />
				</span>
				<span v-else>
					Siege
				</span>
            </span>
            <span class="label mode" v-if="nightfallExpansion">
				<span class="icon-wrapper tooltip-trigger" v-if="expansions.length >= 4">
					<icon
						icon="poison"
						class="icon expansion-icon nightfall"
					/>
					<tooltip value="Nightfall Expansion" />
				</span>
				<span v-else>
					Nightfall
				</span>
            </span>
			<span class="label mode" v-if="shockExpansion">
				<span class="icon-wrapper tooltip-trigger" v-if="expansions.length >= 4">
					<ShockIcon class="icon expansion-icon shock-tactics" />
					<tooltip value="Shock Tactics Expansion" />
				</span>
				<span v-else>
					Shock Tactics
				</span>
            </span>
			<span class="label mode" v-if="championsExpansion">
				<span class="icon-wrapper tooltip-trigger" v-if="expansions.length >= 4">
					<WingedHelmetIcon class="icon expansion-icon champions" />
					<tooltip value="Champions Expansion" />
				</span>
				<span v-else>
					Champions
				</span>
            </span>
            <span class="label mode" v-if="masteryExpansion">
				<span class="icon-wrapper tooltip-trigger" v-if="expansions.length >= 4">
					<fa-icon
						icon="feather"
						class="icon expansion-icon mastery"
					/>
					<tooltip value="Mastery Expansion" />
				</span>
				<span v-else>
					Mastery
				</span>
            </span>
			<span class="label mode" v-if="highSeasExpansion">
				<span class="icon-wrapper tooltip-trigger" v-if="expansions.length >= 4">
					<icon
						icon="ship"
						class="icon expansion-icon high-seas"
					/>
					<tooltip value="High Seas Expansion" />
				</span>
				<span v-else>
					High Seas
				</span>
            </span>
        </div>
        <div class="setting-labels extra">
            <span class="label mode" v-if="
				!nobilityExpansion &&
				!siegeExpansion &&
				!nightfallExpansion &&
				!room.champions &&
				!masteryExpansion &&
				!highSeasExpansion"
			>
                Base
            </span>
            <span class="label mode">
                {{ renderDraftLabel(room.draftType) }}
            </span>
        </div>
        <div class="setting-labels extra" v-if="room.customUnits || room.choosePlayOrder || twoPlayersFullBoard">
            <span class="label mode">
                CPO
            </span>
            <span class="label mode" v-if="room.customUnits">
                Custom units
            </span>
            <span class="label mode" v-if="twoPlayersFullBoard">
               JV
            </span>
        </div>
		<div class="setting-labels extra">
			<span class="label allowed-ranks" v-if="allowedRanks.length">
			<rank
				v-for="rankValue in allowedRanks"
				:key="rankValue"
				:skillRating="rankToSkillRating(rankValue)"
				class="allowed-rank-icon"
			/>
			</span>
		</div>
        <div class="room-join">
            <button
                class="btn btn-primary btn-3d"
                :class="{'btn-small': condensed}"
                v-if="!playerInRoom(room) && room.players.length !== room.numOfPlayers"
                @click="onJoinGame(room)"
            >
                Join
            </button>
            <button class="btn btn-muted btn-3d btn-leave"
                v-if="playerInRoom(room)"
                @click="onLeaveGame(room.id)"
            >
                Leave
            </button>
            <button class="btn btn-primary btn-3d btn-rejoin"
                v-if="playerInRoom(room)"
                @click="onEnterGame(room.id)"
            >
                Rejoin
            </button>
            <button class="btn btn-primary btn-3d"
                v-if="room.players.length === room.numOfPlayers &&
                !playerInRoom(room)"
                @click="onEnterGame(room.id)"
            >
                Spectate
            </button>
        </div>
        <div class="created-at">
            Created {{createdAt}}
        </div>
    </div>
</template>

<script>
import moment from 'moment';

import { IDraftType } from '../Game/Game-types';
import { rankToSkillRating } from '../RanksDropdown/helpers';
import WingedHelmetIcon from '../../assets/winged_helmet.svg';
import ShockIcon from '../../assets/shock_icon.svg';

const RANK_ORDER = {
	bronze: 0,
	silver: 1,
	gold: 2,
	platinum: 3,
	diamond: 4,
	masters: 5,
	grandmaster: 6,
};

export default {
    name: 'Room',
	components: {
		WingedHelmetIcon,
		ShockIcon,
	},
    props: {
        room: {
            type: Object,
            required: true,
        },
        refreshTime: {
            type: [String, Number, Object, Date],
            default: null,
        },
        condensed: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            gameState: {
                waiting_for_players: 'Waiting for players',
                waiting_for_team_selection: 'Selecting teams',
                drafting: 'Drafting units',
                elimination_drafting: 'Elimination drafting',
                ready_to_start: 'Ready to start',
                started: 'Game in progress',
                ended: 'Game has ended',
                selecting_draft_order: 'Selecting draft order',
                selecting_play_order: 'Selecting play order',
            },
            createdAt: moment(this.room.createdAt).fromNow(),
            interval: null,
        };
    },
    watch: {
        refreshTime(current, prev) {
            if (current !== prev) {
                this.createdAt = moment(this.room.createdAt).fromNow();
            }
        },
    },
    computed: {
		expansions() {
			return [
				this.nobilityExpansion,
				this.siegeExpansion,
				this.nightfallExpansion,
				this.masteryExpansion,
				this.highSeasExpansion,
				this.championsExpansion,
				this.shockExpansion,
			].filter(Boolean);
		},
		allowedRanks() {
			const ranks = this.room.settings?.allowedRanks || [];
			return [...ranks].sort((a, b) => RANK_ORDER[a] - RANK_ORDER[b]);
		},
		nobilityExpansion() {
            return this.room.settings?.nobility;
        },
        siegeExpansion() {
            return this.room.settings?.siege;
        },
		nightfallExpansion() {
            return this.room.settings?.nightfall;
        },
        championsExpansion() {
            return this.room.settings?.champions;
        },
        highSeasExpansion() {
            return this.room.settings?.highSeas;
        },
		masteryExpansion() {
            return this.room.settings?.mastery;
        },
        shockExpansion() {
            return this.room.settings?.shock;
        },
        twoPlayersFullBoard() {
            return this.room.settings?.twoPlayersFullBoard;
        },
        blitz() {
            return this.room.settings?.blitz;
        },
        userId() {
            return this.$store.state.loginState.userId;
        },
    },
    methods: {
        async onJoinGame({ id: gameId, passwordRequired }) {
            if (passwordRequired) {
                this.$store.dispatch('setPasswordModal', {
                    description: 'Enter room password',
                    gameId,
                });
                return;
            }

            const options = {
                gameId,
            };

            this.$store.dispatch('joinRoom', options);
            this.$store.dispatch('setSoundReady');
        },
        onEnterGame(gameId) {
            this.$router.push(`/game/${gameId}`);
        },
        onLeaveGame(gameId) {
            this.$store.dispatch('openModal', { type: 'leaveGame', data: { gameId } });
        },
        playerInRoom(room) {
            return room.players.filter(player => player.user.id === this.userId).length;
        },
        renderDraftLabel(draftType) {
            let draftLabel;

            switch (draftType) {
                case IDraftType.DRAFT:
                    draftLabel = 'Draft';
                    break;
                case IDraftType.ELIMINATION_DRAFT:
                    draftLabel = 'E. Draft';
                    break;
                default:
                    draftLabel = 'Random';
            }

            return draftLabel;
        },
        encodeLabel(label) {
            return encodeURIComponent(label);
        },
		rankToSkillRating,
    },
};
</script>

<style scoped lang="scss">
    @use "@/styles/styles.scss" as *;

     .room {
        background: white;
        border-radius: 4px;
        margin: 0 10px 20px;
        display: flex;
        flex-direction: column;
        min-width: 235px;
        text-align: center;
        flex: 1;
        max-width: 250px;

        .content {
            padding: 15px;
            flex-grow: 1;

            .labels {
				align-items: center;
				display: flex;
				gap: 5px;
				justify-content: center;
                margin-bottom: 15px;
            }
        }

        .room-name-container {
            padding: 10px 20px;
            border-top-right-radius: 4px;
            border-top-left-radius: 4px;
            background: $dark-brown;

            .room-name {
                color: $light-gray;
                max-width: 145px;
                display: inline-block;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                line-height: 1.3em;
                vertical-align: middle;
            }
        }

		.icon-wrapper {
			position: relative;

			.expansion-icon {
				width: 12.5px;
				height: auto;
				max-height: 12.5px;
				color: $dark-blue;
				fill: $dark-blue;

				&.nightfall {
					margin-bottom: -1.5px;
				}

				&.champions {
					width: 16px;
					margin-left: -2px;
					margin-right: -2px;
				}
			}
		}

        .room-player {

            .link {
                color: $dark-blue;
            }
        }

        .room-join {
            display: flex;
            padding: 15px;

            .btn-leave {
                margin-right: 8px;
            }

            .btn {
                width: 100%;
            }
        }

        .game-state {

            .game-state-label {
                margin: 5px 0 0 0;
            }
        }

        .setting-labels {
			align-items: center;
			display: flex;
			gap: 5px;
			justify-content: center;
            margin: 0 auto;
            max-width: 90%;

            &.game-modes {

                .label {
                    margin-top: 5px;
                }
            }

            &.extra {
                margin-top: 5px;
            }
        }

        .label {
            font-size: .9em;
            padding: 3px 5px;
            background: $light-gray;
            display: inline-block;
            border-radius: 4px;
            vertical-align: middle;
            border: 1px solid $light-gray;

            &.time-limit-10 {
               border-color: $lightning-yellow;
            }

            &.time-limit-15 {
                border-color: $time-limit-amber;
            }

            &.time-limit-30 {
                border-color: $time-limit-red;
            }

            &.mode {
                font-size: .8em;
            }

            .royal-guard-icon {
                fill: $dark-blue;
                height: 14px;
                vertical-align: middle;
                width: 14px;
            }

            .royal-guard-version {
                vertical-align: middle;
            }

            .blitz-icon {
                color: $herald;
                margin-right: 5px;
            }
        }

        .pwd-required-icon {
            color: $gold;
            margin-left: 5px;
            vertical-align: middle;
        }

		.allowed-rank-icon {
			margin-right: 6px;

			&:last-of-type {
				margin-right: 0;
			}
		}

        .created-at {
            font-size: .8em;
            padding-top: 7px;
            padding-bottom: 7px;
            background: $light-gray;
            font-family: 'Avenir';
            border-bottom-left-radius: 4px;
            border-bottom-right-radius: 4px;
        }
    }

    .room.condensed {
        max-width: 220px;
        min-width: 200px;
        margin: 0 8px 15px;

        .room-name-container {
            padding: 8px 14px;

            .room-name {
                font-size: .95em;
                max-width: 125px;
            }
        }

        .content {
            padding: 10px;

            .labels {
                margin-bottom: 8px;
            }
        }

        .room-player {
            font-size: .85em;

            .link {
                font-size: .85em;
            }
        }

        .label {
            font-size: .75em;
            padding: 2px 4px;
        }

        .setting-labels {
            max-width: 95%;

            &.extra {
                margin-top: 3px;
            }
        }

        .room-join {
            padding: 10px;

            .btn {
                font-size: .85em;
                padding: 6px 8px;
            }
        }

        .created-at {
            font-size: .7em;
            padding: 5px 0;
        }
    }


</style>
